import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Shield, Zap, Gavel, Timer, ChevronLeft, ChevronRight, Users, Play, Monitor, Pin, Pause } from 'lucide-react';
import api from '../utils/api';
import Testimonials from '../components/Testimonials';
import ShareButton from '../components/ShareButton';
import { getImageUrl } from '../utils/imageUrl';
import DigitalNaradTV from '../components/DigitalNaradTV';
import AVXLiveStatsWidget from '../components/AVXLiveStatsWidget';
import SmartVyaparWidget from '../components/SmartVyaparWidget';
import SamadhanHomeWidget from '../components/SamadhanHomeWidget';
import TrialRoomHomeWidget from '../components/TrialRoomHomeWidget';
import CityProHeroWidget from '../components/CityProHeroWidget';
import MarketplaceHeroWidget from '../components/MarketplaceHeroWidget';
import MarketplaceCategoriesWidget from '../components/MarketplaceCategoriesWidget';
import ATPayCard from '../components/ATPayCard';
import { useAuth } from '../context/AuthContext';
import UserDashboard from './user/Dashboard';

// 🎯 Post-login: show the full-featured Dashboard on Home
// (Feb 18, 2026 — user directive: "login ke baad home par dashboard ke sare gradient cards dikhe")
// Pre-login users get the marketing landing below unchanged.
const Home = () => {
  const { user } = useAuth();
  if (user) return <UserDashboard />;
  return <MarketingHome />;
};

const MarketingHome = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [liveAuctions, setLiveAuctions] = useState([]);
  const [auctionIndex, setAuctionIndex] = useState(0);
  const [settings, setSettings] = useState(null);
  const [displayWindows, setDisplayWindows] = useState([]);
  const [activeVideoIndex, setActiveVideoIndex] = useState(0);
  const [isAutoRotating, setIsAutoRotating] = useState(true);
  const [rotationProgress, setRotationProgress] = useState(0);
  const [videoInView, setVideoInView] = useState(true);
  const videoSectionRef = useRef(null);

  useEffect(() => {
    fetchFeaturedProducts();
    fetchLiveAuctions();
    fetchSettings();
    fetchDisplayWindows();
  }, []);

  // 📱 Mobile scroll-jump fix: only rotate videos when section is in viewport.
  // Prevents iframe reload from stealing focus & auto-scrolling the page.
  useEffect(() => {
    if (!videoSectionRef.current || typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver(
      ([entry]) => setVideoInView(entry.isIntersecting),
      { threshold: 0.35 }
    );
    obs.observe(videoSectionRef.current);
    return () => obs.disconnect();
  }, [displayWindows.length]);

  // Auto-rotation effect (paused when video section is scrolled out of view)
  useEffect(() => {
    if (!isAutoRotating || displayWindows.length <= 1 || !videoInView) return;

    const rotateInterval = displayWindows[activeVideoIndex]?.auto_rotate_interval || 10;
    if (rotateInterval === 0) return;

    // Progress bar update (every 100ms)
    const progressInterval = setInterval(() => {
      setRotationProgress(prev => {
        if (prev >= 100) return 0;
        return prev + (100 / (rotateInterval * 10));
      });
    }, 100);

    // Video rotation — preserve scroll position across iframe src change
    const rotationTimer = setTimeout(() => {
      const savedY = window.scrollY;
      setActiveVideoIndex(prev => (prev + 1) % displayWindows.length);
      setRotationProgress(0);
      // Restore scroll on next frames (iframe reload can steal focus on mobile)
      requestAnimationFrame(() => {
        if (Math.abs(window.scrollY - savedY) > 30) window.scrollTo(0, savedY);
      });
      setTimeout(() => {
        if (Math.abs(window.scrollY - savedY) > 30) window.scrollTo(0, savedY);
      }, 250);
    }, rotateInterval * 1000);

    return () => {
      clearInterval(progressInterval);
      clearTimeout(rotationTimer);
    };
  }, [isAutoRotating, activeVideoIndex, displayWindows, videoInView]);

  const handleVideoSelect = useCallback((index) => {
    setActiveVideoIndex(index);
    setRotationProgress(0);
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      const response = await api.get('/products?featured=true');
      setFeaturedProducts(response.data.slice(0, 6));
    } catch (error) {
      console.error('Failed to fetch featured products:', error);
    }
  };

  const fetchLiveAuctions = async () => {
    try {
      const response = await api.get('/auction/list?status=live');
      setLiveAuctions(response.data || []);
    } catch (error) {
      console.error('Failed to fetch live auctions:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchDisplayWindows = async () => {
    try {
      const response = await api.get('/display-windows/homepage');
      setDisplayWindows(response.data || []);
    } catch (error) {
      console.error('Failed to fetch display windows:', error);
    }
  };

  // Helper to extract YouTube embed URL
  const getEmbedUrl = (url) => {
    if (!url) return '';
    const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }
    return url;
  };

  const formatTimeRemaining = (endTime) => {
    const end = new Date(endTime);
    const now = new Date();
    const diff = end - now;
    if (diff <= 0) return 'Ending...';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const nextAuction = () => {
    setAuctionIndex(prev => (prev + 1) % liveAuctions.length);
  };

  const prevAuction = () => {
    setAuctionIndex(prev => (prev - 1 + liveAuctions.length) % liveAuctions.length);
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Digital Narad TV - AI Receptionist (Right Below Header) */}
      <div className="pt-20">
        <DigitalNaradTV />
      </div>

      {/* 🏙️ Anant CityPro Connect — TOP HERO (Feb 2026) */}
      <MarketplaceHeroWidget />
      <MarketplaceCategoriesWidget />
      <CityProHeroWidget />

      {/* 📣 Anant Smart Marketing — quick access */}
      <div className="max-w-6xl mx-auto px-4 mb-3">
        <Link to="/asm" data-testid="home-goto-asm" className="block rounded-2xl border-2 border-fuchsia-400/50 bg-gradient-to-r from-fuchsia-600 via-pink-500 to-cyan-500 p-4 hover:scale-[1.01] transition">
          <div className="flex items-center gap-3">
            <div className="text-3xl">📣</div>
            <div className="flex-1 text-white">
              <div className="text-xs uppercase tracking-widest font-black opacity-90">Anant Smart Marketing</div>
              <div className="text-lg font-black leading-tight">Digital Card · QR · Voice Greeter · Mini-Store</div>
              <div className="text-[10px] opacity-80">Viral no-app share · vCard save · WhatsApp order · 7-tier AT cascade</div>
            </div>
            <div className="text-2xl text-white">→</div>
          </div>
        </Link>
      </div>

      {/* 📱 Anant Vyapaar Master — WhatsApp Marketing quick access */}
      <div className="max-w-6xl mx-auto px-4 mb-3">
        <Link to="/vyapaar-master" data-testid="home-goto-avm" className="block rounded-2xl border-2 border-emerald-400/50 bg-gradient-to-r from-emerald-600 via-teal-500 to-cyan-500 p-4 hover:scale-[1.01] transition">
          <div className="flex items-center gap-3">
            <div className="text-3xl">📱</div>
            <div className="flex-1 text-white">
              <div className="text-xs uppercase tracking-widest font-black opacity-90">Anant Vyapaar Master · New</div>
              <div className="text-lg font-black leading-tight">WhatsApp Templates · Bulk Sender · QR Card</div>
              <div className="text-[10px] opacity-80">600 AT/mo · 6000 AT/yr · L1 15% · L2 8% · L3 5% cashback · 0 server cost</div>
            </div>
            <div className="text-2xl text-white">→</div>
          </div>
        </Link>
      </div>

      {/* 💳 AT Pay Wallet quick card */}
      <div className="max-w-6xl mx-auto px-4 -mt-2 mb-3">
        <ATPayCard label="AT Pay Wallet · Anant FieldPro Connect" />
      </div>

      {/* Anant Smart Vyapar System — ERP Promo (above AVX) */}
      <SmartVyaparWidget />

      {/* AI Trial Room — Virtual Try-On (ABOVE Samadhan Center) */}
      <TrialRoomHomeWidget />

      {/* Samadhan Center — The Dream Project (below Trial Room) */}
      <SamadhanHomeWidget />

      {/* AVX Live Stats Widget — Fantasy Trading Promo */}
      <AVXLiveStatsWidget />

      {/* Hero Section */}
      <section className="hero-gradient pt-12 pb-24 px-6">
        <div className="max-w-7xl mx-auto text-center space-y-8">
          <div className="inline-block px-4 py-2 bg-primary/10 border border-primary/30 rounded-full text-primary text-sm font-semibold mb-4">
            <Sparkles className="w-4 h-4 inline mr-2" />
            Infinite Digital Possibilities
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight" data-testid="hero-title">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-white to-gray-400">
              Premium Digital Products
            </span>
            <br />
            <span className="text-primary">Made Simple</span>
          </h1>
          
          <p className="text-base md:text-lg max-w-2xl mx-auto text-muted-foreground leading-relaxed">
            Discover exclusive software, digital bundles, and courses designed to elevate your digital experience.
            Premium quality, instant delivery, lifetime access.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-8">
            <Link
              to="/products"
              className="btn-primary flex items-center gap-2"
              data-testid="explore-products-button"
            >
              Explore Products
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/courses"
              className="px-8 py-4 bg-secondary/20 text-secondary-foreground border border-secondary/50 hover:bg-secondary/30 hover:border-secondary transition-all duration-300 rounded-full font-semibold"
              data-testid="browse-courses-button"
            >
              Browse Courses
            </Link>
          </div>
        </div>
      </section>

      {/* Live Display Windows Section */}
      {displayWindows.length > 0 && (
        <section ref={videoSectionRef} className="py-16 px-6 bg-gradient-to-r from-primary/5 via-background to-accent/5" data-testid="live-display-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="flex items-center gap-2 px-4 py-2 bg-red-500/20 rounded-full">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                <span className="text-red-500 font-semibold">LIVE</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
                <Monitor className="w-6 h-6 text-primary" />
                Display Window
              </h2>
            </div>

            {/* Main Video Display */}
            <div className="max-w-4xl mx-auto">
              <div className="glass-card overflow-hidden relative">
                {/* Auto-rotation progress bar */}
                {displayWindows.length > 1 && isAutoRotating && (
                  <div className="absolute top-0 left-0 right-0 h-1 bg-white/10 z-10">
                    <div 
                      className="h-full bg-primary transition-all duration-100 ease-linear"
                      style={{ width: `${rotationProgress}%` }}
                    />
                  </div>
                )}
                
                {/* Pinned indicator */}
                {displayWindows[activeVideoIndex]?.is_pinned && (
                  <div className="absolute top-3 left-3 z-10 flex items-center gap-1 px-2 py-1 bg-yellow-500/90 rounded-full text-xs font-semibold text-black">
                    <Pin className="w-3 h-3" />
                    PINNED
                  </div>
                )}
                
                <div className="aspect-video bg-black">
                  <iframe
                    src={getEmbedUrl(displayWindows[activeVideoIndex]?.video_url)}
                    title={displayWindows[activeVideoIndex]?.title || 'Live Display'}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
                <div className="p-4 flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-lg mb-1">{displayWindows[activeVideoIndex]?.title}</h3>
                    {displayWindows[activeVideoIndex]?.description && (
                      <p className="text-sm text-muted-foreground">{displayWindows[activeVideoIndex]?.description}</p>
                    )}
                  </div>
                  
                  {/* Auto-rotation toggle */}
                  {displayWindows.length > 1 && (
                    <button
                      onClick={() => setIsAutoRotating(!isAutoRotating)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        isAutoRotating 
                          ? 'bg-green-500/20 text-green-500 hover:bg-green-500/30' 
                          : 'bg-white/10 text-muted-foreground hover:bg-white/20'
                      }`}
                      title={isAutoRotating ? 'Pause auto-rotation' : 'Start auto-rotation'}
                    >
                      {isAutoRotating ? (
                        <>
                          <Pause className="w-3 h-3" />
                          Auto
                        </>
                      ) : (
                        <>
                          <Play className="w-3 h-3" />
                          Paused
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* Video Thumbnails - if multiple videos */}
              {displayWindows.length > 1 && (
                <div className="flex gap-3 mt-4 overflow-x-auto pb-2">
                  {displayWindows.map((window, idx) => (
                    <button
                      key={window.id}
                      onClick={() => handleVideoSelect(idx)}
                      className={`flex-shrink-0 w-32 rounded-lg overflow-hidden border-2 transition-all ${
                        idx === activeVideoIndex 
                          ? 'border-primary ring-2 ring-primary/30' 
                          : 'border-white/10 hover:border-white/30'
                      }`}
                    >
                      <div className="aspect-video bg-black relative">
                        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                          <Play className={`w-6 h-6 ${idx === activeVideoIndex ? 'text-primary' : 'text-white/70'}`} />
                        </div>
                        {window.is_pinned && (
                          <div className="absolute top-1 right-1">
                            <Pin className="w-3 h-3 text-yellow-500" />
                          </div>
                        )}
                      </div>
                      <div className="p-1.5 bg-white/5">
                        <p className="text-xs truncate">{window.title}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="py-24 px-6 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-8 rounded-3xl bg-gradient-to-br from-white/5 to-transparent border border-white/5 hover:border-white/10 transition-all duration-300" data-testid="feature-card-quality">
              <div className="w-12 h-12 bg-primary/20 rounded-2xl flex items-center justify-center mb-6">
                <Sparkles className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-2xl font-semibold mb-4">Premium Quality</h3>
              <p className="text-muted-foreground leading-relaxed">
                Handpicked digital products and courses from trusted creators. Every item is verified for quality.
              </p>
            </div>
            
            <div className="p-8 rounded-3xl bg-gradient-to-br from-white/5 to-transparent border border-white/5 hover:border-white/10 transition-all duration-300" data-testid="feature-card-delivery">
              <div className="w-12 h-12 bg-secondary/20 rounded-2xl flex items-center justify-center mb-6">
                <Zap className="w-6 h-6 text-secondary" />
              </div>
              <h3 className="text-2xl font-semibold mb-4">Instant Delivery</h3>
              <p className="text-muted-foreground leading-relaxed">
                Get immediate access to your purchases. No waiting, no hassle. Download instantly after payment.
              </p>
            </div>
            
            <div className="p-8 rounded-3xl bg-gradient-to-br from-white/5 to-transparent border border-white/5 hover:border-white/10 transition-all duration-300" data-testid="feature-card-security">
              <div className="w-12 h-12 bg-accent/20 rounded-2xl flex items-center justify-center mb-6">
                <Shield className="w-6 h-6 text-accent" />
              </div>
              <h3 className="text-2xl font-semibold mb-4">Secure Checkout</h3>
              <p className="text-muted-foreground leading-relaxed">
                Safe and encrypted transactions. Multiple payment options including Stripe, Razorpay, and PayPal.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Live Auctions Carousel */}
      {liveAuctions.length > 0 && (
        <section className="py-16 px-6 bg-gradient-to-r from-accent/10 via-background to-primary/10" data-testid="live-auctions-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 px-4 py-2 bg-red-500/20 rounded-full">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                  <span className="text-red-500 font-semibold">LIVE</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold">Auctions</h2>
              </div>
              <Link
                to="/auctions"
                className="flex items-center gap-2 text-primary hover:underline font-medium"
              >
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="relative">
              {/* Carousel Navigation */}
              {liveAuctions.length > 1 && (
                <>
                  <button
                    onClick={prevAuction}
                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20 transition-all"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={nextAuction}
                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20 transition-all"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}

              {/* Auction Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {liveAuctions.slice(auctionIndex, auctionIndex + 3).concat(
                  liveAuctions.slice(0, Math.max(0, 3 - (liveAuctions.length - auctionIndex)))
                ).slice(0, Math.min(3, liveAuctions.length)).map((auction) => (
                  <Link
                    key={auction.id}
                    to={`/auction/${auction.id}`}
                    className="group glass-card overflow-hidden hover:border-accent/50 transition-all"
                    data-testid={`auction-card-${auction.id}`}
                  >
                    <div className="relative aspect-video bg-black/50">
                      {auction.images?.[0] ? (
                        <img
                          src={getImageUrl(auction.images[0])}
                          alt={auction.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-accent/20 to-primary/20">
                          <Gavel className="w-16 h-16 text-white/30" />
                        </div>
                      )}
                      <div className="absolute top-3 left-3 px-3 py-1 bg-green-500 rounded-full text-xs font-semibold text-white flex items-center gap-1">
                        <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                        LIVE
                      </div>
                      <div className="absolute bottom-3 right-3 px-3 py-1.5 bg-black/80 rounded-full text-sm font-mono flex items-center gap-1">
                        <Timer className="w-4 h-4 text-red-500" />
                        {formatTimeRemaining(auction.end_time)}
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-lg mb-2 line-clamp-1 group-hover:text-accent transition-colors">
                        {auction.title}
                      </h3>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-muted-foreground">Current Bid</p>
                          <p className="text-2xl font-bold text-accent">₹{auction.current_bid?.toLocaleString()}</p>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {auction.unique_bidders || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <Gavel className="w-4 h-4" />
                            {auction.total_bids || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Carousel Dots */}
              {liveAuctions.length > 3 && (
                <div className="flex justify-center gap-2 mt-6">
                  {liveAuctions.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setAuctionIndex(idx)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        idx === auctionIndex ? 'bg-primary w-6' : 'bg-white/30 hover:bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Featured Products */}
      {featuredProducts.length > 0 && (
        <section className="py-24 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-4" data-testid="featured-products-title">
                Featured Products
              </h2>
              <p className="text-muted-foreground">Handpicked premium digital products for you</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredProducts.map((product) => (
                <div
                  key={product.id}
                  className="product-card p-6 relative"
                  data-testid={`featured-product-${product.id}`}
                >
                  {/* Share Button */}
                  <div className="absolute top-4 right-4 z-10">
                    <ShareButton
                      type="product"
                      itemId={product.id}
                      itemName={product.name}
                    />
                  </div>
                  
                  <Link to={`/products/${product.id}`}>
                    {product.images && product.images[0] && (
                      <div className="aspect-video rounded-lg overflow-hidden mb-4">
                        <img
                          src={getImageUrl(product.images[0])}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <h3 className="text-xl font-semibold mb-2">{product.name}</h3>
                    <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                      {product.description}
                    </p>
                    <div className="flex items-center gap-3">
                      {product.original_price && (
                        <span className="text-muted-foreground line-through text-sm">
                          ₹{product.original_price}
                        </span>
                      )}
                      <span className="text-2xl font-bold text-primary">
                        ₹{product.price}
                      </span>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
            
            <div className="text-center mt-12">
              <Link
                to="/products"
                className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-full font-semibold hover:bg-primary/90 transition-all glow-gold"
                data-testid="view-all-products-button"
              >
                View All Products
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Testimonials Section */}
      <Testimonials />

      {/* CTA Section */}
      <section className="py-24 px-6 bg-gradient-to-b from-transparent to-primary/10">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight">
            Ready to Transform Your Digital Journey?
          </h2>
          <p className="text-muted-foreground text-lg">
            Join thousands of satisfied customers who trust {settings?.store_name || 'ANANT WEB PORTAL'} for their digital needs.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-full font-semibold hover:bg-primary/90 transition-all glow-gold"
            data-testid="get-started-button"
          >
            Get Started Now
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
